/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 에디터의 캐럿(caret) 위치 제어 + 블록 간 방향키 이동을 정의한다.
 *
 * @file /src/rootin/selection/Caret.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 7. 18.
 */
import { Editor } from '@rootin/core/Editor';
import { Element } from '@rootin/base/Element';
import { Block } from '@rootin/base/Block';

export class Caret {
    /**
     * 에디터의 캐럿(caret) 위치 제어 + 블록 간 방향키 이동을 정의한다.
     *
     * @param editor - 캐럿위치를 제어할 에디터
     */
    constructor(public editor: Editor) {}

    /**
     * 방향키(Tab 포함) 이동을 시도한다.
     *
     * @param direction - 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward'
     * @returns 이벤트를 소비했으면 true (preventDefault 대상)
     */
    move(direction: 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward'): boolean {
        const range = this.editor.selection.getRange();
        if (range === null) {
            return false;
        }

        if (direction === 'up' || direction === 'down') {
            const element = this.editor.focus.getElement();
            if (element === null) {
                return false;
            }

            const down = direction === 'down';
            if (this.isAtEdge(range, element, down) === false) {
                return false;
            }

            if (this.#dispatchToChain(direction)) {
                return true;
            }

            // 이웃이 있으면 손대지 않는다(false) — 네이티브가 블록 경계를 넘으면서도 x좌표를
            // 그대로 보존해준다(파일 헤더 참고). 이웃이 없으면(문서 처음/끝) true 만 반환해
            // 네이티브의 엉뚱한 튕김만 막는다.
            const elements = this.editor.selection.getElements();
            const index = elements.indexOf(element);
            const hasNeighbor = index !== -1 && elements[index + (down ? 1 : -1)] !== undefined;
            return hasNeighbor === false;
        }

        if (direction === 'left' || direction === 'right') {
            // range.startContainer/startOffset 이 아니라 네이티브 Selection 의 focusNode/
            // focusOffset 으로 "옮겨졌는지"를 판정해야 한다 — Shift 로 선택을 확장 중이면 Range 의
            // start 는 앵커 쪽에 고정된 채고 실제로 움직이는 건 focus 쪽 끝이라, start 를 비교하면
            // 캐럿이 한 칸 옮겨갔는데도 "제자리"로 잘못 판정된다. 캐럿만 있을 때(collapsed)는
            // focus === anchor === 캐럿 위치라 이 판정이 그대로 통한다.
            const selection = document.getSelection();
            const focusNode = selection?.focusNode ?? null;
            const focusOffset = selection?.focusOffset ?? 0;

            // 네이티브 이동을 그냥 진행시킨다(여기서 preventDefault 하지 않음 — false 반환).
            // 다음 프레임에 캐럿이 그대로면(=경계에 걸려 못 움직였으면) 그제서야 우리가 개입한다.
            requestAnimationFrame(() => {
                const moved = document.getSelection();
                if (moved !== null && moved.focusNode === focusNode && moved.focusOffset === focusOffset) {
                    if (this.#dispatchToChain(direction) === false) {
                        this.#moveAdjacent(direction === 'right');
                    }
                }
            });
            return false;
        }

        // forward / backward(Tab/Shift+Tab) — 시도할 네이티브 동작이 없으므로 곧장 조상 체인 → 폴백.
        if (this.#dispatchToChain(direction)) {
            return true;
        }
        return this.#moveAdjacent(direction === 'forward');
    }

    /**
     * 캐럿을 엘리먼트의 처음 위치로 옮긴다.
     *
     * @param element - 캐럿을 옮길 대상
     */
    toStart(element: Element): void {
        this.collapse(element, true);
    }

    /**
     * 캐럿을 엘리먼트의 끝 위치로 옮긴다.
     *
     * @param element - 캐럿을 옮길 대상
     */
    toEnd(element: Element): void {
        this.collapse(element, false);
    }

    /**
     * 캐럿위치를 엘리먼트의 처음 또는 끝 위치로 옮긴다.
     *
     * @param element - 캐럿을 옮길 대상
     * @param to_start - true 면 맨 앞, false 면 맨 끝으로 collapse
     */
    collapse(element: Element, to_start: boolean): void {
        if (element.onCaretEdge(to_start) == true) {
            return;
        }

        const el = element.$getElement().getEl();
        el.focus();

        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(to_start);

        // 빈 엘리먼트(placeholder <br> 하나만 있음)에서 캐럿을 그 <br> 뒤에 두면, 타이핑을
        // 시작하는 순간 브라우저가 그 <br>을 지우면서 캐럿을 한 칸 당겨야 하고, 이 재조정이
        // 한글(IME) 조합 중에 캐럿을 건드려 조합을 깨뜨린다(실측 확인) — <br> 앞이면 지워져도
        // 캐럿보다 뒤쪽 일이라 캐럿을 안 건드려도 된다. 그래서 이 상태에선 `to_start` 값과
        // 무관하게 항상 <br> 앞으로 캐럿을 둔다.
        if (el.childNodes.length === 1 && el.firstChild instanceof HTMLBRElement) {
            range.setStart(el, 0);
            range.collapse(true);
        }

        this.editor.selection.setRange(range);
    }

    /**
     * 지금 캐럿이 엘리먼트의 시각적 "가장자리 줄"(맨 윗줄 또는 맨 아랫줄)에 있는지 판정한다.
     *
     * @param range - 현재 캐럿 Range
     * @param element - 판정 대상 엘리먼트
     * @param down - true 면 "맨 아랫줄인가", false 면 "맨 윗줄인가"
     * @returns 가장자리 줄이면 true
     */
    isAtEdge(range: Range, element: Element, down: boolean): boolean {
        const lines = this.lineRects(element);
        if (lines.length === 0) {
            return true;
        }

        const caret = this.caretRect(range);
        if (caret === null) {
            return false;
        }

        const center = (caret.top + caret.bottom) / 2;
        return down === true ? center > lines[lines.length - 1].top : center < lines[0].bottom;
    }

    /**
     * 엘리먼트의 내용이 실제로 화면에서 몇 줄로 표시되는지, 그 각 줄의 사각형을 반환한다.
     *
     * @param element - 대상 엘리먼트
     * @returns 줄 단위 사각형 목록(문서 순서)
     */
    lineRects(element: Element): DOMRect[] {
        const el = element.$getElement().getEl();
        const range = document.createRange();
        range.selectNodeContents(el);
        return Array.from(range.getClientRects());
    }

    /**
     * 지금 캐럿(`range`, collapsed)의 화면상 사각형을 가져온다.
     *
     * @param range - 대상 Range
     * @returns 캐럿의 화면 사각형 (계산 불가하면 null)
     */
    caretRect(range: Range): DOMRect | null {
        const rect = range.getBoundingClientRect();
        if (rect.height > 0 || rect.width > 0 || rect.top !== 0) {
            return rect;
        }

        const rects = range.getClientRects();
        return rects.length > 0 ? rects[0] : null;
    }

    /**
     * 현재 캐럿 위치에 줄바꿈(<br>)을 삽입한다.
     */
    insertLineBreak(): void {
        const range = this.editor.selection.getRange();
        if (range === null) {
            return;
        }

        const element = this.editor.focus.getElement();
        element?.onBeforeEdit();

        // 선택영역이 있으면 먼저 지운다.
        range.deleteContents();

        const br = document.createElement('br');
        range.insertNode(br);

        // insertNode 가 텍스트 노드를 분할하며 br 뒤에 빈 텍스트 노드를 남길 수 있어 정리한다.
        while (
            br.nextSibling !== null &&
            br.nextSibling.nodeType === Node.TEXT_NODE &&
            br.nextSibling.textContent === ''
        ) {
            br.nextSibling.remove();
        }

        // 줄바꿈이 블록 끝이면 단일 <br> 만으로는 빈 줄이 렌더되지 않고 캐럿이 br 앞으로 collapse 된다.
        // 네이티브와 동일하게 보정용 <br>(bogus br) 을 하나 더 둬서 빈 줄을 살린다. (캐럿은 첫 br 뒤)
        if (br.nextSibling === null) {
            br.after(document.createElement('br'));
        }

        range.setStartAfter(br);
        range.collapse(true);

        this.editor.selection.setRange(range);

        if (element !== null) {
            element.onEdit();
        }
    }

    /**
     * 지금 포커스된 블록부터 시작해 조상 방향으로 캐럿 위치이동을 처리한다.
     *
     * @param direction - 캐럿 이동 방향
     * @returns 체인 중 누군가 처리했으면 true
     */
    #dispatchToChain(direction: 'up' | 'down' | 'left' | 'right' | 'forward' | 'backward'): boolean {
        let block: Block = this.editor.focus.getBlock();
        while (true) {
            if (block.onCaretMove(direction)) {
                return true;
            }
            if (block === this.editor.root) {
                return false;
            }
            block = block.getParent().block;
        }
    }

    /**
     * 지금 포커스된 엘리먼트 앞/뒤 엘리먼트로 캐럿을 옮긴다.
     *
     * @param forward - true 면 다음 지점, false 면 이전 지점
     * @returns 실제로 옮겼으면 true
     */
    #moveAdjacent(forward: boolean): boolean {
        const current = this.editor.focus.getElement();
        if (current === null) {
            return false;
        }

        const elements = this.editor.selection.getElements();
        const index = elements.indexOf(current);
        if (index === -1) {
            return false;
        }

        const target = elements[index + (forward ? 1 : -1)] ?? null;
        if (target === null) {
            return false;
        }

        if (forward == true) {
            this.toStart(target);
        } else {
            this.toEnd(target);
        }
        return true;
    }
}
